/**
 * COMPONENT: GroupExtrasModal.jsx
 * Gestiona los extras aplicables a todo el grupo (scope = "per_reservation").
 * Reparte el coste proporcionalmente entre las reservas, sin duplicarlos en individuales.
 */

//COMENZAMOS CAMBIOS DE EXTRA GRUPO DRAFT

import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import {
  buildGroupExtrasAssignmentPayload,
  buildInitialGroupExtraSelection,
  createLatestRequestTracker,
  formatAppliedGroupExtraResult,
  formatDistributedGroupExtraDescription,
  formatRejectedGroupExtraResult,
  getAddableGroupExtras,
  getDraftGroupApiErrorMessage,
  getSelectableGroupExtras,
  getUnavailableAppliedGroupExtras,
  hasStructuredGroupExtraAssignmentResult,
  isPartialGroupExtraAssignmentResult,
  normalizeGroupExtraAssignmentResult,
  resolveDraftGroupExtrasContext,
} from "../reservations/groupDraftUiContracts";

const GroupExtrasModal = ({ show, onClose, groupId, businessUnitId, onSaved }) => {
  const currentContext = resolveDraftGroupExtrasContext({
    groupId,
    businessUnitId,
  });
  const currentContextKey = currentContext.valid
    ? `${currentContext.groupId}:${currentContext.businessUnitId}`
    : "";
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadedSuccessfully, setLoadedSuccessfully] = useState(false);
  const [loadedContextKey, setLoadedContextKey] = useState("");
  const [extras, setExtras] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [saveResult, setSaveResult] = useState(null);
  const [unavailableAppliedIds, setUnavailableAppliedIds] = useState([]);
  const requestTrackerRef = useRef(null);
  if (!requestTrackerRef.current) {
    requestTrackerRef.current = createLatestRequestTracker();
  }

  useEffect(() => {
    const tracker = requestTrackerRef.current;
    const requestToken = tracker.begin();
    setExtras([]);
    setSelectedExtras([]);
    setLoadedSuccessfully(false);
    setLoadedContextKey("");
    setLoading(false);
    setSaving(false);
    setErrorMessage("");
    setSaveResult(null);
    setUnavailableAppliedIds([]);

    if (!show) {
      return () => {
        tracker.invalidate();
      };
    }

    const context = resolveDraftGroupExtrasContext({
      groupId,
      businessUnitId,
    });
    if (!context.valid) {
      setErrorMessage(context.error);
      return () => {
        tracker.invalidate();
      };
    }

    const fetchExtras = async () => {
      try {
        setLoading(true);
        const [catalogResponse, appliedResponse] = await Promise.all([
          axios.get("/api/extras", {
            params: { business_unit_id: context.businessUnitId },
          }),
          axios.get(
            `/api/reservation-draft-groups/${context.groupId}/extras`
          ),
        ]);
        if (!tracker.isCurrent(requestToken)) return;
        if (
          !Array.isArray(catalogResponse.data) ||
          !Array.isArray(appliedResponse.data)
        ) {
          throw new Error("The group extras response was not valid.");
        }

        setExtras(getSelectableGroupExtras(catalogResponse.data));
        setSelectedExtras(
          buildInitialGroupExtraSelection({
            catalog: catalogResponse.data,
            applied: appliedResponse.data,
          })
        );
        setUnavailableAppliedIds(
          getUnavailableAppliedGroupExtras({
            catalog: catalogResponse.data,
            applied: appliedResponse.data,
          }).map((extra) => Number(extra.id))
        );
        setLoadedContextKey(
          `${context.groupId}:${context.businessUnitId}`
        );
        setLoadedSuccessfully(true);
      } catch (err) {
        if (!tracker.isCurrent(requestToken)) return;
        console.error("[GroupExtrasModal] ❌ Error loading extras:", err);
        setExtras([]);
        setSelectedExtras([]);
        setLoadedContextKey("");
        setLoadedSuccessfully(false);
        setErrorMessage(
          getDraftGroupApiErrorMessage(err, "Error loading group extras.")
        );
      } finally {
        if (tracker.isCurrent(requestToken)) setLoading(false);
      }
    };

    void fetchExtras();
    return () => {
      tracker.invalidate();
    };
  }, [show, businessUnitId, groupId]);

  const interactionDisabled =
    loading ||
    saving ||
    !loadedSuccessfully ||
    loadedContextKey !== currentContextKey;

  const clearSaveFeedback = () => {
    setErrorMessage("");
    setSaveResult(null);
  };

  const recordSaveResult = (result) => {
    setSaveResult(result);
    if (result.removed_extra_ids.length > 0) {
      const removedIds = new Set(result.removed_extra_ids.map(Number));
      setUnavailableAppliedIds((current) =>
        current.filter((id) => !removedIds.has(Number(id)))
      );
    }
  };

  const addExtra = (id) => {
    if (interactionDisabled) return;
    const ex = extras.find((e) => String(e.id) === String(id));
    if (!ex) return;
    if (!selectedExtras.find((s) => String(s.id) === String(ex.id))) {
      clearSaveFeedback();
      setSelectedExtras((prev) => [...prev, ex]);
    }
  };

  const removeExtra = (id) => {
    if (interactionDisabled) return;
    clearSaveFeedback();
    setSelectedExtras((prev) => prev.filter((e) => String(e.id) !== String(id)));
  };

  const handleSave = async () => {
    if (interactionDisabled) return;
    const context = resolveDraftGroupExtrasContext({
      groupId,
      businessUnitId,
    });
    if (!context.valid) {
      setLoadedContextKey("");
      setLoadedSuccessfully(false);
      setErrorMessage(context.error);
      return;
    }

    const tracker = requestTrackerRef.current;
    const requestToken = tracker.begin();
    try {
      setSaving(true);
      setErrorMessage("");
      setSaveResult(null);
      const res = await axios.post(
        `/api/reservation-draft-groups/${context.groupId}/assign-extras`,
        buildGroupExtrasAssignmentPayload(selectedExtras)
      );
      if (!tracker.isCurrent(requestToken)) return;
      const normalizedResult = normalizeGroupExtraAssignmentResult(
        res.data,
        selectedExtras
      );
      if (isPartialGroupExtraAssignmentResult(normalizedResult, res.status)) {
        recordSaveResult(normalizedResult);
        if (onSaved) await onSaved(res.data);
        return;
      }
      if (onSaved) await onSaved(res.data);
      if (!tracker.isCurrent(requestToken)) return;
      onClose();
    } catch (err) {
      if (!tracker.isCurrent(requestToken)) return;
      console.error("[GroupExtrasModal] ❌ Error saving group extras:", err);
      const responsePayload = err?.response?.data;
      if (hasStructuredGroupExtraAssignmentResult(responsePayload)) {
        recordSaveResult(
          normalizeGroupExtraAssignmentResult(responsePayload, selectedExtras)
        );
        setErrorMessage("");
        if (onSaved) await onSaved(responsePayload);
      } else {
        setErrorMessage(
          getDraftGroupApiErrorMessage(err, "Error saving group extras.")
        );
      }
    } finally {
      if (tracker.isCurrent(requestToken)) setSaving(false);
    }
  };

  const addableExtras = getAddableGroupExtras(extras, selectedExtras);
  const appliedResultById = new Map(
    (saveResult?.applied || []).map((result) => [Number(result.extra_id), result])
  );
  const rejectedResultById = new Map(
    (saveResult?.rejected || []).map((result) => [Number(result.extra_id), result])
  );

  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1050,
      }}
    >
      <div style={{ zoom: 1, width: "700px", maxWidth: "95%" }}>
        <div
          style={{
            backgroundColor: "#1a1a1a",
            color: "white",
            padding: "20px",
            borderRadius: "6px",
          }}
        >
          <h2 className="text-warning text-center mb-4">
            Manage Group Extras
          </h2>

          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}

          {unavailableAppliedIds.length > 0 && (
            <div className="alert alert-warning" role="status">
              {unavailableAppliedIds.length} previously applied group {unavailableAppliedIds.length === 1 ? "extra is" : "extras are"} no longer available and hidden. {unavailableAppliedIds.length === 1 ? "It" : "They"} will be removed when you save.
            </div>
          )}

          {saveResult &&
            (saveResult.applied.length > 0 ||
              saveResult.rejected.length > 0 ||
              saveResult.removed_extra_ids.length > 0) && (
              <div className="alert alert-warning" role="status">
                <strong>
                  {saveResult.rejected.length > 0
                    ? "Some group extras need attention."
                    : "Group extras processed."}
                </strong>
                <ul className="mb-0 mt-2">
                  {saveResult.applied.map((result) => (
                    <li key={`applied-${result.extra_id}`}>
                      {result.name}: {formatAppliedGroupExtraResult(result)}
                    </li>
                  ))}
                  {saveResult.rejected.map((result) => (
                    <li key={`rejected-${result.extra_id}`}>
                      {result.name}: {formatRejectedGroupExtraResult(result)}
                    </li>
                  ))}
                  {saveResult.removed_extra_ids.length > 0 && (
                    <li>
                      Removed {saveResult.removed_extra_ids.length} unavailable or deselected group {saveResult.removed_extra_ids.length === 1 ? "extra" : "extras"}.
                    </li>
                  )}
                </ul>
                {saveResult.rejected.length > 0 && (
                  <small>Remove or correct the rejected extras, then save again.</small>
                )}
              </div>
            )}

          {loading ? (
            <p className="text-light">Loading extras…</p>
          ) : (
            <div className="row">
              {/* Selector de Extras */}
              <div className="col-md-12 mb-3">
                <label className="form-label">Select Group Extra</label>
                <select
                  className="form-select"
                  disabled={interactionDisabled}
                  onChange={(e) => {
                    if (e.target.value) {
                      addExtra(e.target.value);
                      e.target.value = "";
                    }
                  }}
                >
                  <option value="">Choose an extra to add</option>
                  {addableExtras.map((ex) => (
                    <option key={ex.id} value={ex.id}>
                      {ex.name} — {formatDistributedGroupExtraDescription(ex)}
                    </option>
                  ))}
                </select>
                <small className="text-light">
                  Each displayed amount is the total for the group and is
                  distributed across its reservations. Inactive or invalid
                  extras and zero-value extras are unavailable.
                  Foreign-currency fixed amounts are converted by the server.
                </small>
              </div>

              {/* Lista de seleccionados */}
              <div className="col-md-12 mb-3">
                <label className="form-label">Selected Group Extras</label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    background: "#151515",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    padding: "10px",
                    minHeight: "54px",
                  }}
                >
                  {selectedExtras.length === 0 && (
                    <span className="text-light" style={{ fontSize: "0.9rem" }}>
                      No group extras selected yet.
                    </span>
                  )}
                  {selectedExtras.map((ex) => {
                    const appliedResult = appliedResultById.get(Number(ex.id));
                    const rejectedResult = rejectedResultById.get(Number(ex.id));
                    return (
                      <span
                        key={ex.id}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          border: `1px solid ${rejectedResult ? "#ff4d4d" : "#30d8ff"}`,
                          borderRadius: "12px",
                          padding: "6px 10px",
                          fontSize: "0.85rem",
                        }}
                      >
                        <span>
                          {ex.name} — {formatDistributedGroupExtraDescription(ex)}
                          {appliedResult && (
                            <small style={{ display: "block", color: "#86efac" }}>
                              {formatAppliedGroupExtraResult(appliedResult)}
                            </small>
                          )}
                          {rejectedResult && (
                            <small style={{ display: "block", color: "#fca5a5" }}>
                              Not applied: {formatRejectedGroupExtraResult(rejectedResult)}
                            </small>
                          )}
                        </span>
                        <button
                          type="button"
                          disabled={interactionDisabled}
                          onClick={() => removeExtra(ex.id)}
                          className="btn btn-sm"
                          style={{
                            lineHeight: 1,
                            border: "none",
                            background: "transparent",
                            color: "#ff4d4d",
                            padding: 0,
                            cursor: "pointer",
                          }}
                          aria-label={`Remove ${ex.name}`}
                          title="Remove"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="d-flex justify-content-between gap-3 mt-3">
            <button
              type="button"
              onClick={() => {
                setSelectedExtras([]);
                onClose();
              }}
              style={{
                border: "1px solid #ff4d4d",
                color: "#ff4d4d",
                backgroundColor: "transparent",
                padding: "10px 20px",
                borderRadius: "4px",
                fontSize: "0.95rem",
                fontWeight: "500",
                cursor: "pointer",
                flex: 1,
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={interactionDisabled}
              style={{
                border: "1px solid #30d8ff",
                color: "#30d8ff",
                backgroundColor: "transparent",
                padding: "10px 20px",
                borderRadius: "4px",
                fontSize: "0.95rem",
                fontWeight: "500",
                cursor: "pointer",
                flex: 1,
              }}
            >
              {saving ? "Saving…" : "Save Group Extras"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupExtrasModal;
